import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pubApi, getPublisherToken, clearPublisherToken, API_URL } from '../../api/publisherClient';
import PublisherDashboardLayout from './PublisherDashboardLayout';
import PublisherGuide from '../../components/PublisherGuide';
import Seo from '../../components/Seo';
import TermsModal from './TermsModal';
import Icon from '../../components/Icon';
import CopyButton from '../../components/CopyButton';
import { formatDateTime } from '../../lib/time';

// Tıklama kaynağı gösterim sırası (0 olanlar gizlenir).
const SOURCE_ORDER = ['youtube', 'tiktok', 'twitch', 'kick', 'instagram', 'other', 'unknown'];
const sourceLabel = (t, s) => (s === 'unknown' ? t('publisher.source_unknown') : t(`publisher.platform_${s}`, s));

function AvatarUpload({ publisher, onUploaded }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const res = await pubApi.uploadAvatar(file);
      onUploaded(res.publisher);
    } catch (err) {
      setError(t([`publisher.err_${err.code}`, 'auth.err_generic']));
    } finally {
      setBusy(false);
    }
  };

  const avatarSrc = publisher.avatarUrl ? `${API_URL}${publisher.avatarUrl}` : null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{
        width: 56, height: 56, borderRadius: '50%', overflow: 'hidden',
        background: 'var(--bg-1)', border: '1px solid var(--border)', flexShrink: 0, display: 'grid', placeItems: 'center',
      }}>
        {avatarSrc ? <img src={avatarSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Icon name="users" size={20} style={{ color: 'var(--text-3)' }} />}
      </div>
      <div>
        <div style={{ fontWeight: 600, fontSize: 15 }}>{publisher.displayName || publisher.email}</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
          {(publisher.platforms?.length ? publisher.platforms : [publisher.platform].filter(Boolean))
            .map((p) => <span key={p} className="srv-tag">{t(`publisher.platform_${p}`, p)}</span>)}
          <label className="link" style={{ cursor: 'pointer', fontSize: 12.5 }}>
            {busy ? `${t('common.loading')}…` : t('publisher.avatar_change')}
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={pick} style={{ display: 'none' }} disabled={busy} />
          </label>
        </div>
        {error && <div style={{ color: 'var(--crit)', fontSize: 12, marginTop: 4 }}>{error}</div>}
      </div>
    </div>
  );
}

// "aresmt2" / "Ares PvP" -> "aresmt2" / "ares-pvp" (backend normalizeSlug ile aynı)
function cleanSlug(raw) {
  return String(raw || '')
    .trim().toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function LinkForm({ onCreated }) {
  const { t } = useTranslation();
  const [targetUrl, setTargetUrl] = useState('');
  const [label, setLabel] = useState('');
  const [slug, setSlug] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const preview = cleanSlug(slug);
  const host = window.location.host;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await pubApi.createLink({ targetUrl, label: label || undefined, slug: preview || undefined, kind: 'promo' });
      setTargetUrl('');
      setLabel('');
      setSlug('');
      onCreated();
    } catch (err) {
      setError(t([`publisher.err_${err.code}`, 'auth.err_generic']));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card" onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
      <div>
        <h3 style={{ fontSize: '1rem', margin: 0 }}>{t('publisher.new_link')}</h3>
        <p style={{ color: 'var(--text-3)', fontSize: 12.5, margin: '4px 0 0' }}>{t('publisher.new_link_hint')}</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
        <label className="fld">
          <span>{t('publisher.f_target')}</span>
          <input value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)} type="url" required placeholder="https://tanittigin-server.com" />
        </label>
        <label className="fld">
          <span>{t('publisher.f_slug')}</span>
          <input value={slug} onChange={(e) => setSlug(e.target.value)} maxLength={24} placeholder={t('publisher.f_slug_ph')} />
        </label>
        <label className="fld">
          <span>{t('publisher.f_label')}</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} placeholder={t('publisher.f_label_ph')} />
        </label>
      </div>
      <div className="growth-link">
        <Icon name="link" size={13} />
        <code>{host}/y/{preview || t('publisher.slug_auto')}</code>
      </div>
      <p style={{ color: 'var(--text-3)', fontSize: 12, margin: 0 }}>{t('publisher.f_slug_hint')}</p>
      {error && <p style={{ color: 'var(--crit)', fontSize: 13, margin: 0 }}>{error}</p>}
      <div>
        <button className="btn btn-sm" disabled={busy}>
          <Icon name="plus" size={13} /> {busy ? t('common.loading') : t('publisher.create_link')}
        </button>
      </div>
    </form>
  );
}

function LinkRow({ l, lang, publisher, onUpdated }) {
  const { t } = useTranslation();
  const currentSlug = (l.trackingUrl.split('/y/')[1] || '').replace(/\/+$/, '');
  const sourcesArr = SOURCE_ORDER.map((s) => [s, l.sources?.[s] || 0]).filter(([, n]) => n > 0);
  const linkPlatforms = [
    ...(publisher?.platforms?.length ? publisher.platforms : ['youtube', 'tiktok', 'instagram']),
    'generic',
  ];
  const host = window.location.host;
  const [editing, setEditing] = useState(false);
  const [slug, setSlug] = useState(currentSlug);
  const [label, setLabel] = useState(l.label || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const preview = cleanSlug(slug);

  const startEdit = () => {
    setSlug(currentSlug);
    setLabel(l.label || '');
    setError('');
    setEditing(true);
  };

  const save = async () => {
    setError('');
    setBusy(true);
    try {
      await pubApi.updateLink(l.id, { slug: preview, label });
      setEditing(false);
      onUpdated();
    } catch (err) {
      setError(t([`publisher.err_${err.code}`, 'auth.err_generic']));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{l.label || t('publisher.untitled_link')}</div>
          <div style={{ color: 'var(--text-3)', fontSize: 12.5, marginTop: 2, wordBreak: 'break-all' }}>{l.targetUrl}</div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{l.clicks}</div>
          <div style={{ color: 'var(--text-3)', fontSize: 11 }}>{t('publisher.clicks')}</div>
        </div>
      </div>

      {editing ? (
        <div style={{ display: 'grid', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <label className="fld">
              <span>{t('publisher.f_slug')}</span>
              <input value={slug} onChange={(e) => setSlug(e.target.value)} maxLength={24} placeholder={t('publisher.f_slug_ph')} />
            </label>
            <label className="fld">
              <span>{t('publisher.f_label')}</span>
              <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} placeholder={t('publisher.f_label_ph')} />
            </label>
          </div>
          <div className="growth-link">
            <Icon name="link" size={13} />
            <code>{host}/y/{preview || currentSlug}</code>
          </div>
          {preview && preview !== currentSlug && (
            <p style={{ color: 'var(--warn)', fontSize: 12, margin: 0 }}>{t('publisher.edit_slug_warn')}</p>
          )}
          {error && <p style={{ color: 'var(--crit)', fontSize: 12.5, margin: 0 }}>{error}</p>}
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="btn btn-sm" disabled={busy} onClick={save}>
              {busy ? t('common.loading') : t('common.save')}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>{t('common.cancel')}</button>
          </div>
        </div>
      ) : (
        <>
          {sourcesArr.length > 0 && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-3)' }}>
              {sourcesArr.map(([s, n]) => (
                <span key={s}><strong style={{ color: 'var(--text-2)' }}>{n}</strong> {sourceLabel(t, s)}</span>
              ))}
            </div>
          )}

          <div className="growth-link">
            <Icon name="link" size={13} />
            <code>{l.trackingUrl}</code>
          </div>

          {l.platformUrls && (
            <details style={{ fontSize: 12.5 }}>
              <summary style={{ cursor: 'pointer', color: 'var(--text-2)' }}>{t('publisher.platform_links')}</summary>
              <p style={{ color: 'var(--text-3)', fontSize: 11.5, margin: '6px 0 8px' }}>{t('publisher.platform_links_hint')}</p>
              <div style={{ display: 'grid', gap: 6 }}>
                {linkPlatforms.map((p) => {
                  const url = p === 'generic' ? l.platformUrls.generic : l.platformUrls[p];
                  if (!url) return null;
                  return (
                    <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ minWidth: 78, color: 'var(--text-2)' }}>
                        {p === 'generic' ? t('publisher.platform_generic') : t(`publisher.platform_${p}`, p)}
                      </span>
                      <code style={{ fontSize: 11.5, color: 'var(--text-3)', wordBreak: 'break-all' }}>{url.replace(/^https?:\/\//, '')}</code>
                      <CopyButton text={url} label={t('publisher.copy_link')} />
                    </div>
                  );
                })}
              </div>
            </details>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--text-3)', fontSize: 11.5 }}>{formatDateTime(l.createdAt, lang)}</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" className="btn btn-ghost btn-sm" onClick={startEdit}>{t('publisher.edit_link')}</button>
              <CopyButton text={l.trackingUrl} label={t('publisher.copy_link')} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function PublisherPanel() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const navigate = useNavigate();
  const [publisher, setPublisher] = useState(null);
  const [links, setLinks] = useState(null);
  const [referrals, setReferrals] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [linkRes, refRes] = await Promise.all([pubApi.listLinks(), pubApi.referrals()]);
    setLinks(linkRes.links);
    setReferrals(refRes);
  }, []);

  useEffect(() => {
    if (!getPublisherToken()) {
      navigate('/yayinci/giris', { replace: true });
      return;
    }
    pubApi.me()
      .then((r) => setPublisher(r.publisher))
      .then(load)
      .catch(() => {
        clearPublisherToken();
        navigate('/yayinci/giris', { replace: true });
      })
      .finally(() => setLoading(false));
  }, [navigate, load]);

  const ensureReferralLink = async () => {
    await pubApi.createLink({ targetUrl: `${window.location.origin}/yayincilar`, kind: 'referral', label: t('publisher.referral_link_label') });
    load();
  };

  if (loading || !publisher) {
    return (
      <div className="container" style={{ padding: '80px 0', textAlign: 'center', color: 'var(--text-3)' }}>
        {t('common.loading')}…
      </div>
    );
  }

  // Sözleşme kabul edilmeden panelin GERİSİ hiç render edilmez.
  if (!publisher.termsAcceptedAt) {
    return <TermsModal onAccepted={setPublisher} />;
  }

  const promoLinks = (links || []).filter((l) => l.kind === 'promo');
  const referralLink = (links || []).find((l) => l.kind === 'referral');

  return (
    <PublisherDashboardLayout title={t('publisher.nav_panel')} subtitle={t('publisher.panel_sub')} publisher={publisher}>
      <Seo title="Panel" noindex />
      <div className="card" style={{ marginBottom: 'var(--sp-4)' }}>
        <AvatarUpload publisher={publisher} onUploaded={setPublisher} />
      </div>

      {/* kayıttan sonra ne yapacağını gösteren görsel rehber */}
      <PublisherGuide defaultOpen={promoLinks.length === 0} />

      {/* referans linki — sunucu sahibi merakla geldiğinde paylaşılır */}
      <div className="card" style={{ marginBottom: 'var(--sp-4)' }}>
        <h3 style={{ fontSize: '1rem', marginBottom: 4 }}>{t('publisher.referral_title')}</h3>
        <p style={{ color: 'var(--text-3)', fontSize: 12.5, marginBottom: 12 }}>{t('publisher.referral_desc')}</p>
        {referralLink ? (
          <>
            <div className="growth-link">
              <Icon name="link" size={13} />
              <code>{referralLink.trackingUrl}</code>
            </div>
            <div style={{ display: 'flex', gap: 6, margin: '8px 0 14px' }}>
              <CopyButton text={referralLink.trackingUrl} />
            </div>
          </>
        ) : (
          <button className="btn btn-sm" onClick={ensureReferralLink}>{t('publisher.referral_create')}</button>
        )}
        {referrals && (
          <div className="stat-row" style={{ marginTop: 8 }}>
            <div className="card stat">
              <div className="stat-label"><Icon name="users" size={13} /> {t('publisher.referred')}</div>
              <div className="stat-value">{referrals.totalReferred}</div>
            </div>
            <div className="card stat">
              <div className="stat-label"><Icon name="check" size={13} /> {t('publisher.referred_active')}</div>
              <div className="stat-value">{referrals.activeSubscriptions}</div>
            </div>
          </div>
        )}
      </div>

      {/* link üretici */}
      <div style={{ marginBottom: 'var(--sp-4)' }}>
        <LinkForm onCreated={load} />
      </div>

      <h2 style={{ fontSize: '1.05rem', margin: '0 0 var(--sp-3)' }}>{t('publisher.my_links')}</h2>
      {promoLinks.length === 0 ? (
        <div className="card" style={{ color: 'var(--text-3)', fontSize: 13 }}>{t('publisher.no_links')}</div>
      ) : (
        <div className="list-plain">
          {promoLinks.map((l) => <LinkRow key={l.id} l={l} lang={lang} publisher={publisher} onUpdated={load} />)}
        </div>
      )}
    </PublisherDashboardLayout>
  );
}
